import { useCallback, useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { buildPaginatedEndpoint } from '@/helpers/api'

import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'
import { wordTemplatesQueryKeys } from '@/hooks/word-templates/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UseWordTemplatesArgs<T> {
  orgId: UUID | ''
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  selector?: (data: PaginatedResponse<File>) => T
}

export const fetchWordTemplates = async (
  orgId: UUID | '',
  accessToken: string,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<File>> => {
  if (!orgId) {
    throw new Error('No organization ID provided.')
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/word-templates`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<File>
}

export const useWordTemplates = <T = File[]>({
  orgId,
  enabled = true,
  staleTime = FILE_STALE_TIME,
  refetchOnMount = true,
  refetchOnWindowFocus = true,
  selector,
}: UseWordTemplatesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const select = useCallback(
    (data: PaginatedResponse<File>) => {
      if (selector) {
        return selector(data)
      }
      return data.data.sort((a, b) =>
        a.name.localeCompare(b.name),
      ) as unknown as T
    },
    [selector],
  )

  const query = useQuery({
    queryFn: () => fetchWordTemplates(orgId, accessToken, { limit: 10000 }),
    queryKey: wordTemplatesQueryKeys.organization({ orgId }),
    staleTime,
    refetchOnMount,
    refetchOnWindowFocus,
    enabled: enabled && orgId !== '' && orgId !== undefined,
    select,
  })

  return query
}

interface UseWordTemplatesPrefetchArgs {
  orgId: UUID | ''
}

export const useWordTemplatesPrefetch = ({
  orgId,
}: UseWordTemplatesPrefetchArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: wordTemplatesQueryKeys.organization({ orgId }),
      queryFn: () => fetchWordTemplates(orgId, accessToken, { limit: 10000 }),
    })
  }, [orgId, accessToken, queryClient])
}

interface UseWordTemplatesCountArgs {
  orgId: UUID | ''
}

interface WordTemplatesCount {
  count: number
}

const selectWordTemplatesCount = (
  data: PaginatedResponse<File>,
): WordTemplatesCount => {
  return { count: data.count }
}

export const useWordTemplatesCount = ({ orgId }: UseWordTemplatesCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  return useQuery({
    queryKey: wordTemplatesQueryKeys.count({ orgId }),
    // TODO: remove this limit
    queryFn: () => fetchWordTemplates(orgId, accessToken, { limit: 10000 }),
    enabled: !!orgId && !!accessToken,
    select: selectWordTemplatesCount,
  })
}
